import { computed, ref } from "vue";
import { stripHtml, toEuro } from "@spoferan/spoferan-ts-core";
import { useI18n, useNuxtApp } from "#imports";
export function useValidation(value, rules, options = { valid: true, type: "text" }) {
  const { t, te } = useI18n();
  const { $date } = useNuxtApp();
  const errorBag = ref({});
  const isValid = ref(options.valid);
  const inputType = computed(() => {
    switch (options.type) {
      case "tel":
        return "text";
    }
    return options.type ?? "text";
  });
  function clearValidation() {
    errorBag.value = {};
  }
  function setValidationError(errors) {
    errorBag.value = errors;
    isValid.value = !Object.keys(errorBag.value).length;
  }
  function validate(stopOnFirstError = true) {
    let failed = {};
    if (Object.prototype.hasOwnProperty.call(rules.value, "validate") && !rules.value.validate) {
      setValidationError(failed);
      return isValid.value;
    }
    for (let i = 0; i < Object.keys(rules.value).length; i++) {
      const ruleName = Object.keys(rules.value)[i];
      const ruleValue = Object.values(rules.value)[i];
      let result = true;
      switch (ruleName) {
        case "custom":
          result = custom(ruleValue);
          break;
        case "min":
          result = min(ruleValue);
          break;
        case "max":
          result = max(ruleValue);
          break;
        case "required":
          result = required(ruleValue);
          break;
        case "accepted":
          result = accepted(ruleValue);
          break;
        case "equal":
          result = equal(ruleValue);
          break;
        case "date":
          result = date(ruleValue);
          break;
        case "phone":
          result = phone();
          break;
        default:
          throw new Error(`Unknown validation rule '${ruleName}'`);
      }
      if (result !== true) {
        failed[ruleName] = result;
      }
      if (stopOnFirstError && Object.keys(failed).length) {
        break;
      }
    }
    setValidationError(failed);
    return isValid.value;
  }
  function custom(callable) {
    return isUnsetValue(value.value) || callable(value.value);
  }
  function min(min2) {
    if (isUnsetValue(min2)) {
      return true;
    }
    const type = inputType.value;
    if (type === "date" || type === "datetime") {
      return isUnsetValue(value.value) || !min2 || $date(min2).isSameOrBefore(value.value, type === "datetime") || getMinErrorMessage(min2);
    } else if (type === "number" || type === "cents") {
      return isUnsetValue(value.value) || parseFloat(value.value) >= parseFloat(min2) || getMinErrorMessage(min2);
    }
    const inputValue = value.value ? stripHtml(value.value) : null;
    return isUnsetValue(inputValue) || inputValue.length >= min2 || getMinErrorMessage(min2);
  }
  function getMinErrorMessage(min2) {
    min2 = formatValueForType(min2);
    return te("validation.min." + inputType.value) ? t("validation.min." + inputType.value, { min: min2 }) : t("validation.min.text", { min: min2 });
  }
  function max(max2) {
    if (isUnsetValue(max2)) {
      return true;
    }
    let type = inputType.value;
    if (type === "date" || type === "datetime") {
      return isUnsetValue(value.value) || !max2 || $date(value.value).isSameOrBefore(max2, type === "datetime") || getMaxErrorMessage(max2);
    } else if (type === "number" || type === "cents") {
      return isUnsetValue(value.value) || parseFloat(value.value) <= parseFloat(max2) || getMaxErrorMessage(max2);
    }
    const inputValue = value.value ? stripHtml(value.value) : null;
    return isUnsetValue(inputValue) || inputValue.length <= max2 || getMaxErrorMessage(max2);
  }
  function getMaxErrorMessage(max2) {
    max2 = formatValueForType(max2);
    return te("validation.max." + inputType.value) ? t("validation.max." + inputType.value, { max: max2 }) : t("validation.max.text", { max: max2 });
  }
  function formatValueForType(value2) {
    if (!value2) {
      return value2;
    }
    if (inputType.value === "datetime") {
      return $date(value2).format("LLL");
    }
    if (inputType.value === "date") {
      return $date(value2).format("LL");
    }
    if (inputType.value === "cents") {
      return toEuro(parseInt(value2));
    }
    return value2;
  }
  function required(isRequired) {
    return !isUnsetValue(value.value) || !isRequired || t("validation.required");
  }
  function accepted(requiresAccept) {
    return !requiresAccept || [true, "true", 1, "1", "on"].indexOf(value.value) > -1 || t("validation.accepted");
  }
  function equal(equal2) {
    return value.value && value.value === equal2 || t("validation.equal");
  }
  function date(format = null) {
    if (!value.value) {
      return true;
    }
    let inputFormat = format;
    if (Array.isArray(format)) {
      inputFormat = format[1] ?? format[0];
      format = format[0];
    }
    return $date(value.value, format).isValid() || (inputFormat ? t("validation.date_format", { format: inputFormat }) : t("validation.date"));
  }
  function phone() {
    return !value.value || !value.value.length || value.value.startsWith("+") || value.value.startsWith("0") || t("validation.phone");
  }
  function isUnsetValue(value2) {
    return [null, void 0, ""].includes(value2);
  }
  return {
    validate,
    clearValidation,
    setValidationError,
    isValid,
    errorBag
  };
}
